import React from "react";
import Box from "@mui/material/Box/Box";
import GenieText from "../../../../components/core/genie-text/GenieText";
import GenieStepper from "../../../../components/core/genie-stepper/GenieStepper";
import { Cart, CartLineItem } from "../../../../types/api/Cart";
import { formatCurrency } from "../../../../utils/currency";
import useAddLineItem from "../../../../queries/routes/useAddLineItem";
import useDeleteLineItem from "../../../../queries/routes/useDeleteLineItem";
import { useParams } from "react-router-dom";
import useUser from "../../../../queries/routes/useUser";

interface LineItemProps {
  lineItem: CartLineItem;
  cart: Cart;
  readonly?: boolean;
}

function LineItem({ cart, lineItem, readonly }: LineItemProps) {
  const { org_id } = useParams();
  const { data: user } = useUser();

  const { mutate: addLineItem, isLoading: isAddLineItemLoading } =
    useAddLineItem(cart?.id || "", org_id!, user?.organizations[0].id!);
  const { mutate: deleteLineItem, isLoading: isDeleteLineItemLoading } =
    useDeleteLineItem(lineItem.id || "", org_id!, user?.organizations[0].id!);

  return (
    <Box
      border={1}
      p={2}
      mb={2}
      borderRadius={1}
      sx={{ borderColor: "grey.300" }}
    >
      <Box display={"flex"}>
        <img
          width={"80px"}
          height={"80px"}
          src={
            lineItem.product.media.length > 0
              ? lineItem.product.media[0].media.cloudinary_url
              : `https://source.unsplash.com/random/80x80`
          }
          alt={"Product"}
        />
        <Box sx={{ ml: 2 }}>
          <GenieText fontSize={"tiny"} fontWeight={"bold"}>
            {lineItem.product.name}
          </GenieText>
          {!readonly && (
            <>
              <Box display={"flex"} alignItems={"center"} mt={1}>
                <GenieStepper
                  quantity={lineItem.quantity || null}
                  loading={isAddLineItemLoading || isDeleteLineItemLoading}
                  add={(quantity) => {
                    addLineItem({
                      product_id: lineItem.product.id,
                      quantity: quantity
                        ? quantity
                        : lineItem
                        ? lineItem.quantity + 1
                        : 1,
                    });
                  }}
                  remove={() => {
                    lineItem.quantity === 1
                      ? deleteLineItem(lineItem!.id)
                      : addLineItem({
                          product_id: lineItem.product.id,
                          quantity: lineItem ? lineItem.quantity - 1 : 1,
                        });
                  }}
                />
                <GenieText
                  fontSize={"tiny"}
                  style={{ ml: 2 }}
                  fontWeight={"bold"}
                >
                  {formatCurrency(lineItem.product.unit_price_in_cents)}
                </GenieText>
              </Box>
              <GenieText
                fontSize={"tiny"}
                style={{ mt: 1, cursor: "pointer" }}
                onClick={() => {
                  deleteLineItem(lineItem.id);
                }}
                underline
              >
                Remove
              </GenieText>
            </>
          )}
          {readonly && (
            <Box mt={1}>
              <GenieText fontSize={"tiny"} subdued={true}>
                {lineItem.quantity} items
              </GenieText>
              <GenieText fontSize={"tiny"} subdued={true}>
                {formatCurrency(lineItem.product.unit_price_in_cents)} per item
              </GenieText>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default LineItem;
